import { Injectable } from '@angular/core';
import {
  Person,
  SearchPersonsData200Response,
  SearchPersonsDataPreview200Response,
  SearchPersonsDataPreviewRequest,
  SearchPersonsDataRequest,
  SearchService,
} from 'ldt-people-api';
import { QuickBuild, QuickBuildService, UIFilterGroup } from './quick-build.service';
import { map, Observable, of, tap } from 'rxjs';
import { Log } from '../logger.service';
import { AuthService } from '../auth/service/auth.service';
import * as hash from 'object-hash';
import * as dayjs from 'dayjs';
export interface ReportResponse {
  count: number;
  data: any;
}

export interface PeopleDataResponse {
  count: number;
  people: Person[];
  pagination_token?: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class PeopleReportService {
  private apiCache = new Map<string, any>(); // Map to store API responses
  private contextCache = new Map<string, boolean>(); // Tracks which cache keys belong to the current context for filtering purposes
  private cacheSizeInBytes = 0;
  private maxCacheSize = 100; // Maximum number of cached items allowed

  constructor(
    private reportService: SearchService,
    private auth: AuthService
  ) {
    this.auth.$getSelectedOrg.subscribe((org) => {
      this.clearCache();
    });
  }

  // Add the API response to the cache FIFO eviction
  private addToCache(key: string, response: any): void {
    if (this.apiCache.size >= this.maxCacheSize) {
      const firstKey = this.apiCache.keys().next().value!;
      this.apiCache.delete(firstKey);
    }
    this.apiCache.set(key, response);

    const newItemSize = PeopleReportService.getObjectSize(response);
    this.cacheSizeInBytes += newItemSize;
    const cacheStats = this.getCacheStats();
    Log.d('Cache stats:', cacheStats);
  }

  // Add method to calculate size of object in bytes
  private static getObjectSize(obj: any): number {
    const str = JSON.stringify(obj);
    return str.length * 2;
  }

  // Add method to get current cache size
  private getCacheStats(): { sizeInBytes: number; sizeInMB: number; itemCount: number } {
    return {
      sizeInBytes: this.cacheSizeInBytes,
      sizeInMB: this.cacheSizeInBytes / 1024 / 1024,
      itemCount: this.apiCache.size,
    };
  }

  getCache(): { [key: string]: any } {
    return Object.fromEntries(this.apiCache);
  }

  setCache(cache: { [key: string]: any }) {
    this.apiCache = new Map(Object.entries(cache));
  }

  clearCache() {
    this.apiCache.clear();
    this.cacheSizeInBytes = 0;
  }

  startCacheContext() {
    this.contextCache.clear();
  }

  getContextCache(): { [key: string]: any } {
    // only return from apicache if the key is in the context cache
    return Object.fromEntries(
      Array.from(this.apiCache.entries()).filter(([key]) => this.contextCache.has(key))
    );
  }

  // Get report data by report ID and quick build
  getReportDataById(reportId: string, quickBuild: QuickBuild): Observable<ReportResponse> {
    Log.d('getReportDataById-quickBuild', quickBuild, reportId);
    const apiFilters = QuickBuildService.rootFilterGroupToAPIFilters(
      quickBuild.rootFilterGroup,
      reportId
    );

    Log.d('getReportDataById-apiFilters', apiFilters);

    let req: SearchPersonsDataRequest = {
      filters: [apiFilters[0]],
      size: 0,
    };

    const hash = JSON.stringify(req);
    this.contextCache.set(hash, true);
    if (this.apiCache.has(hash)) {
      return of(this.apiCache.get(hash));
    }

    const legacyHash = this.checkLegacyHashWithoutArrDepGroupBy(req);
    if (legacyHash) {
      return of(legacyHash);
    }

    return this.reportService
      .searchPersonsData(this.auth.getSelectedOrgIdValue, undefined, req)
      .pipe(
        map((res: any) => {
          return {
            count: res.count,
            data: res.report_results,
          };
        }),
        tap((res: any) => {
          this.addToCache(hash, res);
        })
      );
  }

  getCountOnly(quickBuild: QuickBuild): Observable<ReportResponse> {
    // We get a copy of the quick build, remove all the reports since we don't need them
    const qbCopy = QuickBuildService.copyQuickBuildWithoutReports(quickBuild);
    const apiFilters = QuickBuildService.rootFilterGroupToAPIFilters(qbCopy.rootFilterGroup);

    Log.d('PeopleReportService getCountOnly apiFilters', apiFilters);

    let req: SearchPersonsDataRequest = {
      filters: [apiFilters[0]],
      size: 0,
    };

    const hash = JSON.stringify(req);
    this.contextCache.set(hash, true);
    if (this.apiCache.has(hash)) {
      return of(this.apiCache.get(hash));
    }

    return this.reportService
      .searchPersonsData(this.auth.getSelectedOrgIdValue, undefined, req)
      .pipe(
        map((res: any) => {
          return {
            count: res.count,
            data: [],
          };
        }),
        tap((res: any) => {
          this.addToCache(hash, res);
        })
      );
  }

  public getPeopleData(
    quickBuild: QuickBuild,
    preview: boolean,
    sortBy: string | undefined,
    size: number | undefined,
    paginationToken: string | undefined
  ): Observable<PeopleDataResponse> {
    const apiFilters = QuickBuildService.rootFilterGroupToAPIFilters(quickBuild.rootFilterGroup);

    const sampleReq: SearchPersonsDataPreviewRequest = {
      filters: [apiFilters[0]],
      sort_by: sortBy,
    };

    const req: SearchPersonsDataRequest | SearchPersonsDataPreviewRequest = {
      filters: [apiFilters[0]],
      sort_by: sortBy,
      size: size,
      pagination_token: paginationToken,
    };

    // This is notable - this class is generally used for client-side caching to avoid http overhead for all the API calls.
    // For this particular case, we are using the cache primarily for public links. Because we know all public links will
    // be "preview", we want to save the people data as if it came from a "preview" request so that if a public link is used,
    // this cache will be used. So we have a separate hash key for the preview and we also mutate the response (below) and
    // save it to that key. That ensure that even if this request/response was for a non-preview request, the cache will be used
    const sampleHashToSave = hash(sampleReq);
    const hashToSave = hash(req);

    // We only save the preview has to the context cache
    this.contextCache.set(sampleHashToSave, true);
    const hashToLookup = preview ? sampleHashToSave : hashToSave;

    if (this.apiCache.has(hashToLookup)) {
      return of(this.apiCache.get(hashToLookup));
    }

    // Check for legacy hash format - only for PALs, so just for preview mode (before we switched to `hash`)
    // This logic will be disabled after 2026-01-01 for cleanup
    if (preview && dayjs().isBefore(dayjs('2026-01-01'))) {
      const cachedResult = this.checkLegacyHashWithDateRange(sampleReq);
      if (cachedResult) {
        return of(cachedResult);
      }
    }

    const apiCall = preview
      ? this.reportService.searchPersonsDataPreview(this.auth.getSelectedOrgIdValue, sampleReq)
      : this.reportService.searchPersonsData(this.auth.getSelectedOrgIdValue, undefined, req);
    return apiCall.pipe(
      map((res: SearchPersonsDataPreview200Response | SearchPersonsData200Response) => {
        return {
          count: res.count || 0,
          people: res.people || [],
          pagination_token: 'pagination_token' in res ? res.pagination_token : undefined,
        };
      }),
      tap((res: PeopleDataResponse) => {
        // Save both the fill and the preview result to the cache
        this.addToCache(hashToSave, res);
        this.addToCache(sampleHashToSave, this.fullResultToSampleResult(res));
      })
    );
  }

  // Check legacy hash format with iterative date checking from now until 2025-08-01
  private checkLegacyHashWithDateRange(sampleReq: SearchPersonsDataPreviewRequest): any | null {
    const targetDate = dayjs('2025-08-01');
    let currentDate = dayjs();

    // Start from current date and work backwards to 2025-08-01
    while (currentDate.isAfter(targetDate) || currentDate.isSame(targetDate)) {
      const dateString = currentDate.format('YYYY-MM-DD');

      let legacyHash = JSON.stringify(sampleReq);
      // Replace "now/M" with the specific date we're testing
      legacyHash = legacyHash.replace(/"date_to":"now\/M"/g, `"date_to":"${dateString}"`);
      legacyHash = legacyHash.replace(/"date_from":"now\/M"/g, `"date_from":"${dateString}"`);

      if (this.apiCache.has(legacyHash)) {
        return this.apiCache.get(legacyHash);
      }

      // Move to previous day
      currentDate = currentDate.subtract(1, 'day');
    }

    return null;
  }

  private checkLegacyHashWithoutArrDepGroupBy(
    sampleReq: SearchPersonsDataPreviewRequest
  ): any | null {
    let legacyHash = JSON.stringify(sampleReq);

    const output = legacyHash.replace(
      /("report"\s*:\s*{[^}]*"params"\s*:\s*{[^}]*?),\s*"group_by":\[\]/,
      '$1'
    );

    if (this.apiCache.has(output)) {
      return this.apiCache.get(output);
    }

    return null;
  }

  // Mutate the response to a publicly allowed result, removing sensitive fields
  private fullResultToSampleResult(res: PeopleDataResponse): PeopleDataResponse {
    return {
      count: res.count,
      people: res.people.map((p) => ({
        ...p,
        first_name: undefined,
        last_name: undefined,
        name: undefined,
        email_address: undefined,
        cell_phone_number: undefined,
        location_details: undefined,
        linkedin: undefined,
        jobs: p.jobs?.slice(0, 3),
      })),
      pagination_token: undefined,
    };
  }

  // Get report data with report details
  getReportDataWithReportDetails(
    reportId: string,
    reportType: string,
    reportParams: any,
    reportTarget: string,
    quickBuild: QuickBuild
  ): Observable<ReportResponse> {
    Log.d('getReportDataWithReportDetails-quickBuild', quickBuild, reportId, reportTarget);

    // If the report target is not provided, use the root filter group id
    if (!reportTarget && quickBuild.rootFilterGroup.id) {
      reportTarget = quickBuild.rootFilterGroup.id;
    }

    // We get a copy of the quick build, remove all the reports since we can only do one at a time,
    // and we add the one we want back into it
    const qbCopy = QuickBuildService.copyQuickBuildWithoutReports(quickBuild);
    const report = {
      id: reportId,
      name: reportType,
      params: reportParams,
    };

    Log.d('getReportDataWithReportDetails-report', report);
    PeopleReportService.addReportToQuickBuild(qbCopy, reportTarget, report);
    Log.d('getReportDataWithReportDetails-qbCopy', qbCopy);

    return this.getReportDataById(report.id, qbCopy);
  }

  // Add a report to the quick build at the given location
  private static addReportToQuickBuild(qb: QuickBuild, reportTarget: string, report: any) {
    let groupToModify = PeopleReportService.findGroupToModify(qb.rootFilterGroup, reportTarget);

    if (!groupToModify) {
      groupToModify = PeopleReportService.findFirstJobsGroup(qb.rootFilterGroup);

      if (!groupToModify) {
        throw new Error('Group to modify not found');
      }
    }

    groupToModify.reports = [report];
  }

  // Find the group to modify in the quick build
  private static findGroupToModify(
    group: UIFilterGroup,
    reportTarget: string
  ): UIFilterGroup | undefined {
    if (group.id === reportTarget) {
      return group;
    }

    const filterGroupFilters = group.filters.filter((f) =>
      QuickBuildService.isUIFilterGroup(f)
    ) as UIFilterGroup[];

    for (const filter of filterGroupFilters) {
      const result = PeopleReportService.findGroupToModify(filter, reportTarget);
      if (result) {
        return result;
      }
    }

    return undefined;
  }

  // Find the first jobs group in the quick build
  private static findFirstJobsGroup(group: UIFilterGroup): UIFilterGroup | undefined {
    if (group.isJobsGroup) {
      return group;
    }

    const filterGroupFilters = group.filters.filter((f) =>
      QuickBuildService.isUIFilterGroup(f)
    ) as UIFilterGroup[];

    for (const filter of filterGroupFilters) {
      const result = PeopleReportService.findFirstJobsGroup(filter);
      if (result) {
        return result;
      }
    }

    return undefined;
  }
}
